/**
 * ColumnAddBasicFields — pure presentational name/type/null/default/identity
 * inputs for the add-column form. Lifted out of `ColumnAddFormBody` to keep
 * the orchestrator under the 200-line budget once the Slice A3 generated
 * section landed.
 *
 * State is owned by the parent. The `defaultDisabled` and `identityDisabled`
 * flags reflect mutual exclusivity with the Generated section — when the user
 * fills a generated expression, default + identity render but accept no input.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice A2 (initial), § Slice A3 (extracted).
 */
import type { SqlEngine } from '../../../../sql-grammar/model';
import { Checkbox } from '../../../../../components/ui/checkbox';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../../../../components/ui/radio-group';

export type AddColumnNullableMode = 'unspecified' | 'null' | 'not-null';

export interface ColumnAddBasicFieldsProps {
  readonly engine: SqlEngine;
  readonly testId: string;
  readonly name: string;
  readonly setName: (next: string) => void;
  readonly type: string;
  readonly setType: (next: string) => void;
  readonly nullableMode: AddColumnNullableMode;
  readonly setNullableMode: (next: AddColumnNullableMode) => void;
  readonly defaultExpr: string;
  readonly setDefaultExpr: (next: string) => void;
  readonly defaultDisabled: boolean;
  readonly identity: boolean;
  readonly setIdentity: (next: boolean) => void;
  readonly identityDisabled: boolean;
  readonly showSqliteIdentityHint: boolean;
}

const NULLABLE_LABELS: Readonly<Record<AddColumnNullableMode, string>> = {
  unspecified: 'Engine default',
  null: 'NULL',
  'not-null': 'NOT NULL',
};

const NULLABLE_OPTIONS: ReadonlyArray<AddColumnNullableMode> = ['unspecified', 'null', 'not-null'];

export function ColumnAddBasicFields(props: ColumnAddBasicFieldsProps) {
  const {
    testId, name, setName, type, setType, nullableMode, setNullableMode,
    defaultExpr, setDefaultExpr, defaultDisabled,
    identity, setIdentity, identityDisabled, showSqliteIdentityHint,
  } = props;

  return (
    <>
      <div className="flex flex-col gap-1">
        <Label htmlFor={`${testId}-name`} className="text-xs">
          Name <span className="text-destructive">*</span>
        </Label>
        <Input
          id={`${testId}-name`}
          data-testid={`${testId}-name`}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="column_name"
          className="h-8 font-mono text-xs"
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor={`${testId}-type`} className="text-xs">
          Type <span className="text-destructive">*</span>
        </Label>
        <Input
          id={`${testId}-type`}
          data-testid={`${testId}-type`}
          value={type}
          onChange={(e) => setType(e.target.value)}
          placeholder="INT / VARCHAR(100) / numeric(10,2)"
          className="h-8 font-mono text-xs"
        />
      </div>

      <fieldset className="flex flex-col gap-1">
        <legend className="text-xs">Nullability</legend>
        <RadioGroup
          value={nullableMode}
          onValueChange={(v) => setNullableMode(v as AddColumnNullableMode)}
          className="flex items-center gap-2 text-xs"
        >
          {NULLABLE_OPTIONS.map((mode) => (
            <label key={mode} className="flex items-center gap-1">
              <RadioGroupItem value={mode} data-testid={`${testId}-nullable-${mode}`} />
              <span>{NULLABLE_LABELS[mode]}</span>
            </label>
          ))}
        </RadioGroup>
      </fieldset>

      <div className="flex flex-col gap-1">
        <Label htmlFor={`${testId}-default`} className="text-xs">Default (optional)</Label>
        <Input
          id={`${testId}-default`}
          data-testid={`${testId}-default`}
          value={defaultExpr}
          disabled={defaultDisabled}
          title={defaultDisabled ? 'Generated columns cannot also carry a DEFAULT.' : undefined}
          onChange={(e) => setDefaultExpr(e.target.value)}
          placeholder="expression — e.g. 0, CURRENT_TIMESTAMP"
          className="h-8 font-mono text-xs disabled:opacity-60"
        />
      </div>

      <div className="flex flex-col gap-1">
        <label
          className="flex items-center gap-2 text-xs"
          title={identityDisabled ? 'Generated columns cannot also be identity columns.' : undefined}
        >
          <Checkbox
            checked={identity}
            disabled={identityDisabled}
            onCheckedChange={(checked) => setIdentity(checked === true)}
            data-testid={`${testId}-identity`}
          />
          <span className={identityDisabled ? 'text-muted-foreground' : ''}>
            Identity / auto-increment
          </span>
        </label>
        {showSqliteIdentityHint ? (
          <span
            className="text-[10px] text-muted-foreground"
            data-testid={`${testId}-identity-sqlite-hint`}
          >
            SQLite requires the type to include `INTEGER PRIMARY KEY` for AUTOINCREMENT.
          </span>
        ) : null}
      </div>
    </>
  );
}
